"use client";

import { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { StandardFormSelect } from "@/components/common/StandardFormInput";
import { fetchMetafieldsRequest } from "@/store/slices/metafield.slice";
import { AppDispatch, RootState } from "@/store/store";
import type { Metafield, MetafieldType } from "@/types/models/metafield";

export interface MetafieldBindingValue {
  appliedTo: "products" | "collections";
  metafieldKey: string;
  metafieldType: MetafieldType;
}

interface MetafieldSelectorProps {
  label?: string;
  appliedTo: "products" | "collections";
  value?: MetafieldBindingValue;
  onValueChange: (value: MetafieldBindingValue) => void;
  helperText?: string;
  error?: string;
}

const metafieldLabel = (metafield: Metafield): string =>
  `${metafield.key} · ${metafield.type}`;

export const MetafieldSelector: React.FC<MetafieldSelectorProps> = ({
  label = "Metafield",
  appliedTo,
  value,
  onValueChange,
  helperText,
  error,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { items: metafields, loading, loaded } = useSelector(
    (state: RootState) => state.metafields,
  );

  useEffect(() => {
    if (!loaded && !loading) {
      dispatch(fetchMetafieldsRequest({}));
    }
  }, [dispatch, loaded, loading]);

  const scopedMetafields = useMemo(
    () => metafields.filter((metafield) => metafield.applied_to === appliedTo),
    [appliedTo, metafields],
  );

  const selectedMetafield = useMemo(
    () => scopedMetafields.find((metafield) => metafield.key === value?.metafieldKey),
    [scopedMetafields, value?.metafieldKey],
  );

  const options = useMemo(() => {
    const nextOptions = scopedMetafields.map((metafield) => ({
      label: metafieldLabel(metafield),
      value: metafield.key,
    }));

    if (
      value?.metafieldKey &&
      !nextOptions.some((option) => option.value === value.metafieldKey)
    ) {
      nextOptions.unshift({
        label: `Missing: ${value.metafieldKey}`,
        value: value.metafieldKey,
      });
    }

    return nextOptions;
  }, [scopedMetafields, value?.metafieldKey]);

  const handleMetafieldChange = (metafieldKey: string) => {
    const matchedMetafield = scopedMetafields.find(
      (metafield) => metafield.key === metafieldKey,
    );

    onValueChange({
      appliedTo,
      metafieldKey,
      metafieldType: matchedMetafield?.type ?? value?.metafieldType ?? "text",
    });
  };

  return (
    <div className="space-y-2">
      <StandardFormSelect
        label={label}
        value={value?.metafieldKey || ""}
        onValueChange={handleMetafieldChange}
        options={options}
        placeholder={
          scopedMetafields.length > 0
            ? "Select metafield"
            : "No metafields available"
        }
        error={error}
        helperText={helperText}
        disabled={loading || scopedMetafields.length === 0}
      />
      {selectedMetafield && (
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Label className="text-xs text-muted-foreground">
            Bound to {appliedTo}
          </Label>
          <Badge variant="secondary">{selectedMetafield.type}</Badge>
        </div>
      )}
      {!selectedMetafield && scopedMetafields.length === 0 && (
        <p className="text-xs text-muted-foreground">
          No {appliedTo} metafields are available yet.
        </p>
      )}
    </div>
  );
};
